import { useHttpClient, useTranslation } from '@wix/fe-essentials/react';
import { createApp as createAppHttp } from '@wix/ambassador-devcenter-apps-v1-app/http';
import { queryMyApps } from '@wix/ambassador-devcenter-myapps-v1-my-app/http';
import { useEssentials } from '@wix/fe-essentials-standalone';
import { useState } from 'react';
import type { V1App } from '@wix/dev-center-pages-common';
import { useNotifications } from '../useNotifications';

export const useCreateApp = () => {
  const httpClient = useHttpClient();
  const { panoramaClient } = useEssentials();
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const { showToast } = useNotifications();
  const { t } = useTranslation();

  const captureError = (error: Error) => {
    panoramaClient.errorMonitor().reportError(error);
    console.error(error);
  };

  const getNewAppName = (totalApps: number) => {
    return `My New App-${totalApps}`;
  };

  const fetchTotalAppCount = async () => {
    const response = await httpClient.request(
      queryMyApps({ countTotal: true }),
    );
    const total = response.data?.total ?? 0;
    return total;
  };

  const createAppApi = async (name: string) => {
    const response = await httpClient.request(
      createAppHttp({
        app: {
          name,
        },
      }),
    );

    await new Promise((res) => setTimeout(res, 3000));

    return response.data;
  };
  const createApp = async (): Promise<{ appId: string }> => {
    setIsCreatingApp(true);
    try {
      if (isCreatingApp) {
        throw new Error('Already creating app');
      }
      const totalAppCount = await fetchTotalAppCount();
      const appName = getNewAppName(totalAppCount + 1);
      const result = await createAppApi(appName);
      const app = result?.app;
      return { appId: app?.id };
    } catch (error) {
      showToast({
        theme: 'error',
        message: t('app.template-page.modal.self-hosted.step2.create-error'),
      });
      captureError(error as Error);
      throw new Error('Failed to create app');
    } finally {
      setIsCreatingApp(false);
    }
  };

  return {
    createApp,
    isCreatingApp,
  };
};
